// ─────────────────────────────────────────────────────────────────────────────
// "Choose your branch": a card per branch with its photo, address, hours, open
// or closed, and how far it is from the customer's saved address.
//
// Header.js opens it:
//   * by itself on Home every time the site is opened, until the customer picks
//     a branch on that page load (utils/branchChoice.js explains the "chosen"
//     flag), with no way to dismiss it, because "which branch?" decides the
//     menu, prices and stock;
//   * from "View all branches" in the header's branch drop-down, dismissible.
//
// It does not switch branches itself. It hands the tapped branch to Header,
// which saves it on the server first (utils/selectBranch.js) and keeps the QR
// "Change restaurant?" check. `busyValue` is the branch being switched to, so
// its card can show a spinner and the others stay disabled meanwhile.
//
// COST: nothing here calls Google. Photos come from GET /api/restaurant/all
// (image_url) and distances from the free OSRM router Home.js already uses.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import {
  sortBranches,
  isClosed,
  hoursText,
  distanceText,
  isOutOfDeliveryArea,
  displayBranchName,
} from "../utils/branchChoice";
import "./branchPicker.css";

function BranchPhoto({ src, name }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="bp-photo bp-photo--empty" aria-hidden="true">
        {(name || "?").trim().charAt(0)}
      </div>
    );
  }
  return (
    <img
      className="bp-photo"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

export default function BranchPicker({
  show,
  required = false,
  stores,
  selectedValue,
  radiusKm,
  busyValue = null,
  onPick,
  onClose,
}) {
  const sorted = useMemo(() => sortBranches(stores), [stores]);
  const busy = busyValue != null;

  return (
    <Modal
      show={show}
      onHide={required ? undefined : onClose}
      backdrop={required ? "static" : true}
      keyboard={!required}
      centered
      size="lg"
      fullscreen="sm-down"
      scrollable
      dialogClassName="bp-dialog"
      aria-labelledby="bp-title"
    >
      <Modal.Header closeButton={!required} className="bp-header">
        <div>
          <Modal.Title id="bp-title" as="h5" className="bp-title">
            Choose your branch
          </Modal.Title>
          <p className="bp-sub">
            Your menu, prices and delivery come from the branch you pick. You can change it any
            time from the top of the page.
          </p>
        </div>
      </Modal.Header>
      <Modal.Body className="bp-body">
        <ul className="bp-list">
          {sorted.map((store) => {
            const name = displayBranchName(store.label);
            const closed = isClosed(store);
            const hours = hoursText(store);
            const far = !closed && isOutOfDeliveryArea(store, radiusKm);
            const dist = distanceText(store);
            const current = selectedValue != null && String(selectedValue) === String(store.value);
            const thisBusy = busy && String(busyValue) === String(store.value);
            return (
              <li key={store.value}>
                <button
                  type="button"
                  className={`bp-card${current ? " is-current" : ""}${closed ? " is-closed" : ""}`}
                  onClick={() => onPick(store)}
                  disabled={busy}
                  aria-pressed={current}
                >
                  <BranchPhoto src={store.image_url} name={name} />
                  <span className="bp-info">
                    <span className="bp-name">{name}</span>
                    {store.restaurant_address && (
                      <span className="bp-address">{store.restaurant_address}</span>
                    )}
                    <span className="bp-meta">
                      <span className={`bp-status ${closed ? "bp-status--closed" : "bp-status--open"}`}>
                        {closed ? "Closed now" : "Open"}
                      </span>
                      {hours && <span>{hours}</span>}
                      {dist && <span>{dist}</span>}
                    </span>
                    {far && (
                      <span className="bp-note">Too far to deliver to your saved address. Takeaway still works.</span>
                    )}
                  </span>
                  <span className="bp-action" aria-hidden="true">
                    {thisBusy ? (
                      <Spinner animation="border" size="sm" />
                    ) : current ? (
                      <span className="bp-tag">Selected</span>
                    ) : (
                      <span className="bp-chev">›</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </Modal.Body>
    </Modal>
  );
}
